import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface BaseOption {
  id: string;
}

interface AutocompleteInputProps<TOption extends BaseOption> {
  value: string;
  onChange: (value: string) => void;
  onSelect: (option: TOption | null) => void;
  options: TOption[];
  getOptionLabel: (option: TOption) => string;
  placeholder?: string;
  isLoading?: boolean;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
}

function AutocompleteInput<TOption extends BaseOption>(props: AutocompleteInputProps<TOption>) {
  const {
    value,
    onChange,
    onSelect,
    options,
    getOptionLabel,
    placeholder = 'Buscar...',
    isLoading = false,
    emptyMessage = 'No se encontraron resultados.',
    disabled = false,
    className,
  } = props;

  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // Open dropdown when options are available and input is focused
  useEffect(() => {
    if (options.length > 0 && value.length >= 2) {
      setOpen(true);
      setHighlightedIndex(0);
    } else {
      setOpen(false);
    }
  }, [options, value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleSelect = (option: TOption) => {
    onSelect(option);
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % options.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + options.length) % options.length);
        break;
      case 'Enter':
        event.preventDefault();
        if (options[highlightedIndex]) {
          handleSelect(options[highlightedIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  const handleInputFocus = () => {
    if (options.length > 0 && value.length >= 2) {
      setOpen(true);
    }
  };

  const showDropdown = open && !disabled;

  return (
    <div ref={containerRef} className="relative w-full">
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        autoComplete="off"
      />

      {showDropdown && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="max-h-60 overflow-y-auto">
            {options.length > 0 ? (
              <>
                {isLoading && (
                  <div className="flex items-center gap-2 border-b px-3 py-1.5 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Buscando…
                  </div>
                )}
                {options.map((option, index) => (
                  <button
                    key={option.id}
                    type="button"
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition hover:bg-gray-50',
                      index === highlightedIndex && 'bg-gray-100'
                    )}
                    onClick={() => handleSelect(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <span className="truncate">{getOptionLabel(option)}</span>
                  </button>
                ))}
              </>
            ) : isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Buscando…
              </div>
            ) : value.length >= 2 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export { AutocompleteInput };
export type { BaseOption };
